import { ProductsTable } from "@/components/tables/products";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/lib/api/api-error";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import {
  ProductService,
  type ProductSyncRequest,
} from "@/services/product-service";
import type { Warehouse } from "@/services/types/products";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Loader2, RefreshCw, Upload, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const SYNC_POLL_INTERVAL_MS = 5_000; // 5 seconds

export const Route = createFileRoute("/_main/products/")({
  component: RouteComponent,
  loader: async () => {
    const [warehouses] = await Promise.all([
      ProductService.listWarehouses(),
    ]);
    return { warehouses };
  },
  head: () => ({
    meta: buildMetadata({
      title: "Products",
      description:
        "Manage all your SKUs, pricing, and inventory levels in one place",
      keywords: ["products", "inventory", "sku", "stock"],
    }),
  }),
});

/**
 * Check if a sync request is currently in progress.
 */
function isSyncInProgress(syncRequest: ProductSyncRequest | null): boolean {
  if (!syncRequest) {
    return false;
  }
  return (
    syncRequest.status === "pending" || syncRequest.status === "processing"
  );
}

function RouteComponent() {
  const { warehouses } = Route.useLoaderData() as {
    warehouses: Warehouse[];
  };

  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isWarehouseUser = user?.supplierRole === "warehouse_user";
  const wasSyncingRef = useRef(false);

  // Track the current sync request ID (set when user triggers sync or when we detect one in progress)
  const [currentSyncId, setCurrentSyncId] = useState<number | "latest">(
    "latest",
  );

  // Poll for sync status - uses ID if available, otherwise checks latest
  const syncStatusQuery = useQuery({
    queryKey: ["product-sync-status", currentSyncId],
    queryFn: () => ProductService.getSyncStatus(currentSyncId),
    refetchInterval: (query) => {
      // Poll every 3 seconds while pending/processing
      const syncRequest = query.state.data;
      return isSyncInProgress(syncRequest ?? null)
        ? SYNC_POLL_INTERVAL_MS
        : false;
    },
  });

  const isSyncing = isSyncInProgress(syncStatusQuery.data ?? null);

  // Refresh products when sync completes
  useEffect(() => {
    // Detect transition from syncing (true) to not syncing (false)
    if (wasSyncingRef.current && !isSyncing) {
      const syncRequest = syncStatusQuery.data;
      if (syncRequest?.status === "completed") {
        const created = syncRequest.products_created ?? 0;
        const updated = syncRequest.products_updated ?? 0;
        toast.success(
          `Sync completed! Created: ${created}, Updated: ${updated}`,
        );
      } else if (syncRequest?.status === "failed") {
        toast.error(`Sync failed: ${syncRequest.log || "Unknown error"}`);
      }
      // Invalidate all products queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["products"] });
      // Reset to polling latest for future syncs
      setCurrentSyncId("latest");
    }
    wasSyncingRef.current = isSyncing;
  }, [isSyncing, syncStatusQuery.data, queryClient]);

  const syncMutation = useMutation({
    mutationFn: () => ProductService.syncProducts(),
    onSuccess: (data) => {
      if (data.status === "started" && data.sync_request) {
        toast.success(data.message);
        // Track this specific sync request
        setCurrentSyncId(data.sync_request.id);
      } else if (data.status === "already_running" && data.sync_request) {
        toast.info(data.message);
        // Track the existing sync request
        setCurrentSyncId(data.sync_request.id);
      } else if (data.status === "error") {
        toast.error(data.message);
      }
    },
    onError: (error: unknown) => {
      let message = "Failed to start sync. Please try again.";
      if (error instanceof ApiError) {
        const body = error.body as { message?: string } | undefined;
        if (body?.message) {
          message = body.message;
        }
      }
      toast.error(message);
      console.error("Sync error:", error);
    },
  });

  const isButtonDisabled = syncMutation.isPending || isSyncing;

  const [globalTextFilter, setGlobalTextFilter] = useState("");
  const [globalWarehouseFilter, setGlobalWarehouseFilter] = useState("");
  const [filteredResults, setFilteredResults] = useState(warehouses);

  const filterResults = ({warehouseFilter, textFilter} : {warehouseFilter: string, textFilter: string}) => {
    const arr:Warehouse[] = [];
    if (warehouseFilter == "-") {
      arr.push(...warehouses);
    } else {
      warehouses.forEach(w => {
        if (w.name == warehouseFilter) {
          arr.push(w);
        }
      });
    }

    const matched = arr.map(w => ({
      ...w, // Copy parent properties
      product_stocks: w.product_stocks.filter(p => (p.name?.toLowerCase().includes(textFilter) || p.sku.toLowerCase().includes(textFilter))) // Filter nested array
    }));
    
    setFilteredResults(matched);
  }

  return (
    <div className="space-y-6 p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="font-semibold text-2xl tracking-tight">Products</h1>
          <p className="text-muted-foreground text-sm">
            Manage all your SKUs, pricing, and inventory levels in one place
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" size="lg" className="rounded-xl" asChild>
            <Link to="/products/upload">
              <Upload className="mr-2 h-4 w-4" />
              Upload Document
            </Link>
          </Button>
          {!isWarehouseUser && (
            <Button
              onClick={() => syncMutation.mutate()}
              disabled={isButtonDisabled}
              size="lg"
              className="cursor-pointer rounded-xl bg-foreground text-background hover:bg-foreground/90"
            >
              {isButtonDisabled ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {isSyncing ? "Syncing in Progress..." : "Sync Stock Levels"}
            </Button>
          )}
        </div>
      </div>


      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <Select
            onValueChange={(v) => {
              setGlobalWarehouseFilter(v);
              filterResults({warehouseFilter: v, textFilter: globalTextFilter});
            }}
          >
            <SelectTrigger className="h-9 w-full rounded-lg">
              <SelectValue placeholder="Select Warehouse" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="-">Select Warehouse</SelectItem>
              {
                warehouses.map(w => {
                  return <SelectItem value={w.name} key={w.name}>{w.name}</SelectItem>;
                })
              }
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="relative">
            <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by SKU or name..."
              value={globalTextFilter}
              onChange={(e) => {
                const keyword = e.target.value.toLowerCase();
                setGlobalTextFilter(keyword);
                filterResults({textFilter: keyword, warehouseFilter: globalWarehouseFilter});
              }}
              className="w-full pl-9 md:w-[300px]"
            />
          </div>
        </div>
      </div>


      {filteredResults.map(w => {
        return <ProductsTable
          data={w.product_stocks}
          warehouse={w}
          key={w.id}
        />
      })}
      
    </div>
  );
}
